import type {
  IConnectorApprovalPolicyChangesResponse,
  IConnectorApprovalSettingsResponse,
  IConnectorCatalogEntry,
  IOrgConnectorApprovalPolicyResponse,
  IUpsertConnectorApprovalSettingPayload,
  IUpsertOrgConnectorApprovalPolicyPayload,
} from "../types/connector-approval";
import type { ApiClient } from "./client";

const BASE_PATH = "/user/connector-approval-settings";

export class ConnectorApprovalApi {
  constructor(private apiClient: ApiClient) {}

  async getSettings(): Promise<IConnectorApprovalSettingsResponse> {
    const response =
      await this.apiClient.get<IConnectorApprovalSettingsResponse>(BASE_PATH);
    return response.data;
  }

  async getCatalog(): Promise<IConnectorCatalogEntry[]> {
    const response = await this.apiClient.get<IConnectorCatalogEntry[]>(
      `${BASE_PATH}/catalog`,
    );
    return response.data;
  }

  async upsertSetting(
    payload: IUpsertConnectorApprovalSettingPayload,
  ): Promise<IConnectorApprovalSettingsResponse> {
    const response =
      await this.apiClient.put<IConnectorApprovalSettingsResponse>(
        BASE_PATH,
        payload,
      );
    return response.data;
  }

  async resetSetting(
    provider: string,
  ): Promise<IConnectorApprovalSettingsResponse> {
    const response =
      await this.apiClient.delete<IConnectorApprovalSettingsResponse>(
        `${BASE_PATH}/${provider}`,
      );
    return response.data;
  }

  async getOrgPolicy(
    organizationUuid: string,
  ): Promise<IOrgConnectorApprovalPolicyResponse> {
    const response =
      await this.apiClient.get<IOrgConnectorApprovalPolicyResponse>(
        `${BASE_PATH}/org/${organizationUuid}`,
      );
    return response.data;
  }

  // NOTE: The connector-approval floors that apply to the current user across
  // every org they belong to or own (strictest per connector) — used to surface
  // the org minimum in the per-user settings panel so members (and owners)
  // can't unknowingly pick a looser option. No org id needed; the server
  // resolves the user's orgs, which also covers owners who aren't project
  // members.
  async getEffectiveFloor(): Promise<IOrgConnectorApprovalPolicyResponse> {
    const response =
      await this.apiClient.get<IOrgConnectorApprovalPolicyResponse>(
        `${BASE_PATH}/effective-floor`,
      );
    return response.data;
  }

  // NOTE: NSAI-4976 — when and how the org minimum level for this connector
  // changed, across every org the caller belongs to or owns. Newest first.
  async listPolicyChanges(
    provider: string,
  ): Promise<IConnectorApprovalPolicyChangesResponse> {
    const response =
      await this.apiClient.get<IConnectorApprovalPolicyChangesResponse>(
        `${BASE_PATH}/policy-changes`,
        { params: { provider } },
      );
    return response.data;
  }

  async upsertOrgPolicy(
    organizationUuid: string | undefined,
    payload: IUpsertOrgConnectorApprovalPolicyPayload,
  ): Promise<IOrgConnectorApprovalPolicyResponse> {
    // NOTE: Fail fast instead of hitting `/org/` with an empty uuid (which
    // would 404) when the organization isn't loaded yet.
    if (!organizationUuid) {
      throw new Error("Organization UUID is required");
    }
    const response =
      await this.apiClient.put<IOrgConnectorApprovalPolicyResponse>(
        `${BASE_PATH}/org/${organizationUuid}`,
        payload,
      );
    return response.data;
  }
}
